import classnames from 'classnames';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isFeatureActive } from '@helpers';
import { Header } from 'palette/Typography';
import { fetchFavoriteBrands } from '@common/Brands/helpers/favoriteBrand';
import { setFavoriteBrand } from '@common/Favorites/actions/favoritesActions';
import { FavoritesBrandButton } from '@common/Favorites/containers/FavoritesBrandButton';
import { useConfig } from '@common/Hooks/useConfig';
import { useTranslation } from '@common/Hooks/useTranslation';
import { Breadcrumbs, ProductsCountInfo } from '@common/Listing/components/';
import { ListingDescription } from '@common/Listing/containers/ListingDescription';
import { SPONSORED_PRODUCT_MODAL_ID } from '@common/Product/Sponsor/constants/modalIds';
import { SponsoredProductsModal } from '@common/Product/Sponsor/SponsoredProductsModal';
import { ReactOnRailsRootComponent } from '@common/ReactOnRails';
import '../styles/ListingTopHead.scss';

export function ListingTopHead({
    breadcrumbItems,
    customClass,
    description,
    favorite,
    header,
    categoryName,
    isDescriptionExpanded
}) {
    const config = useConfig();
    const dispatch = useDispatch();
    const translate = useTranslation();
    const currentPage = useSelector(({ pagination }) => pagination.currentPage);
    const pageTitle = useSelector(({ listing }) => listing.seoData?.page_title);
    const hasToShowDescription = description && currentPage === 1;

    const headerToRender = useMemo(() => {
        if (pageTitle) {
            return pageTitle;
        }

        return header;
    }, [
        header,
        pageTitle
    ]);

    const addFavoritesToState = (favorites) => {
        const payload = {
            brands: favorites,
            gender: favorite?.gender
        };
        dispatch(setFavoriteBrand(payload));
    };

    useEffect(() => {
        fetchFavoriteBrands((response) => {
            addFavoritesToState(response[favorite?.gender]);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favorite?.gender]);

    return (
        <div
            className={classnames('listing-top-head', customClass, {
                'listing-top-head--description': hasToShowDescription
            })}
        >
            {breadcrumbItems && breadcrumbItems.length > 0
                && (<Breadcrumbs items={breadcrumbItems} />)}
            <div className="listing-top-head__header">
                <div className="listing-top-head__title">
                    <Header
                        className="listing-top-head__text"
                        color="strong"
                        level="1"
                    >
                        {headerToRender}
                    </Header>
                    {favorite && (() => {
                        const {
                            gender, id, name, brandazId
                        } = favorite;

                        return (
                            <FavoritesBrandButton
                                brandAzId={brandazId?.toString()}
                                brandGender={gender}
                                brandId={`${id}`}
                                brandName={name}
                                className="listing-top-head__favorite-btn"
                            />
                        );
                    })()}
                </div>
                <div className="listing-top-head__product-info-container">
                    <ProductsCountInfo
                        categoryName={categoryName}
                        className="listing-top-head__products-count"
                    />
                    {isFeatureActive('PLT-36479-sponsored-products', config) && (
                        <SponsoredProductsModal
                            className="listing-top-head__sponsored-icon"
                            id={SPONSORED_PRODUCT_MODAL_ID.listingTopHead}
                            linkLabel={translate('listing_products.tooltip_link')}
                            linkURL={translate('listing_products.tooltip_url')}
                            message={translate('listing_products.tooltip_text')}
                            title={translate('listing_products.tooltip_title')}
                        />
                    )}
                </div>
            </div>
            {hasToShowDescription
                && (
                    <ListingDescription
                        customClass="listing-top-head__description"
                        description={description}
                        descriptionId="top_desc"
                        isDescriptionExpanded={isDescriptionExpanded}
                    />
                )}
        </div>
    );
}

export const ListingTopHeadStandalone = ReactOnRailsRootComponent(ListingTopHead, { withReduxStore: true });
